/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect } from 'react';
import { jsPDF } from 'jspdf';
import { OrdenProduccion, DisposicionProducto, User } from '../types';
import { formatColombiaDateTime } from '../utils/colombiaTime';
import { ArrowUpDown, CheckSquare, AlertTriangle, XOctagon, Plus, Clipboard, UserCheck, Calendar, FileSpreadsheet, Download, Play, RefreshCw, Pencil, Search, X } from 'lucide-react';
import { motion } from 'motion/react';
// @ts-ignore
import insumedicLogo from '../assets/images/insumedic_logo_v2_1779469546019.png';

const formatFechaHora = (isoStr?: string) => {
  if (!isoStr) return 'N/A';
  try {
    const d = new Date(isoStr);
    if (isNaN(d.getTime())) return isoStr;
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch (e) {
    return isoStr;
  }
};

interface DisposicionProductosProps {
  ordenes: OrdenProduccion[];
  disposiciones: DisposicionProducto[];
  currentUser: User;
  users: User[];
  onAddDisposicion: (nuevaDisp: DisposicionProducto) => void;
  onUpdateOrdenStatus: (
    id: string, 
    newStatus: 'Pendiente por Aceptar' | 'Aceptada' | 'Cerrada' | 'Cancelada',
    extraFields?: Partial<OrdenProduccion>
  ) => void;
  onUpdateDisposicion?: (updatedDisp: DisposicionProducto) => void;
  isReadOnly?: boolean;
}

export default function DisposicionProductos({
  ordenes,
  disposiciones,
  currentUser,
  users,
  onAddDisposicion,
  onUpdateOrdenStatus,
  onUpdateDisposicion,
  isReadOnly = false,
}: DisposicionProductosProps) {
  // Select active order
  const [selectedOrdenId, setSelectedOrdenId] = useState('');
  const [estadoDisposicion, setEstadoDisposicion] = useState<'Aprobado' | 'Rechazado' | 'En Cuarentena'>('Aprobado');
  const [cantidadDispuesta, setCantidadDispuesta] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // Editing State
  const [editingDispId, setEditingDispId] = useState<string | null>(null);

  // Fast Search State
  const [searchTerm, setSearchTerm] = useState('');

  const filteredDisposiciones = disposiciones.filter((disp) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase().trim();
    return (
      disp.numeroOrden.toLowerCase().includes(term) ||
      disp.id.toLowerCase().includes(term) ||
      disp.nombreMedicamento.toLowerCase().includes(term) ||
      disp.estadoDisposicion.toLowerCase().includes(term) ||
      disp.responsable.toLowerCase().includes(term) ||
      disp.observaciones.toLowerCase().includes(term)
    );
  });

  const handleStartEditDisp = (disp: DisposicionProducto) => {
    setEditingDispId(disp.id);
    setSelectedOrdenId(disp.ordenId);
    setEstadoDisposicion(disp.estadoDisposicion);
    setCantidadDispuesta(disp.cantidadDispuesta.toString());
    setObservaciones(disp.observaciones);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEditDisp = () => {
    setEditingDispId(null);
    setSelectedOrdenId('');
    setEstadoDisposicion('Aprobado');
    setCantidadDispuesta('');
    setObservaciones('');
  };

  // Selected Order Object
  const selectedOrden = ordenes.find((o) => o.id === selectedOrdenId);

  // Set default quantity when order selection changes
  useEffect(() => {
    if (selectedOrden) {
      if (editingDispId) {
        return; // Do not overwrite state loaded by the editor flow
      }
      const totalDisposed = disposiciones
        .filter((d) => d.ordenId === selectedOrden.id)
        .reduce((sum, d) => sum + d.cantidadDispuesta, 0);
      const pending = selectedOrden.tamanoLote - totalDisposed;

      setCantidadDispuesta(pending.toString());
      setObservaciones(`Acondicionamiento físico correcto del lote ${selectedOrden.loteRP}. Verificado cumple estándar asistencial.`);

      // Auto-populate the Auxiliar de Reempaque field if the currently logged in user has that role
      if (currentUser && currentUser.cargo === 'Auxiliar de Reempaque' && (!selectedOrden.elaboradoPor || selectedOrden.elaboradoPor !== currentUser.nombreCompleto)) {
        onUpdateOrdenStatus(selectedOrden.id, selectedOrden.estadoOrden, { elaboradoPor: currentUser.nombreCompleto });
      }
    } else {
      if (!editingDispId) {
        setCantidadDispuesta('');
        setObservaciones('');
      }
    }
  }, [selectedOrdenId, ordenes, disposiciones, currentUser, onUpdateOrdenStatus, editingDispId]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMsg('');
    setErrorMsg('');

    if (isReadOnly) {
      setErrorMsg('Modo Consulta Activo: Su suscripción se encuentra vencida. No se pueden registrar ni modificar disposiciones de productos.');
      return;
    }

    // If editing, we do not strictly require selecting an active production order
    if (!editingDispId && (!selectedOrdenId || !selectedOrden)) {
      setErrorMsg('Debe seleccionar una Orden de Producción activa.');
      return;
    }

    if (selectedOrden) {
      if (selectedOrden.estadoOrden !== 'Aceptada' && currentUser.username.toUpperCase() !== 'JUAN.VALENCIA' && currentUser.cargo !== 'Director Técnico y de Calidad') {
        setErrorMsg(`La orden seleccionada actualmente se encuentra "${selectedOrden.estadoOrden}". Debe ser ACEPTADA antes de poder registrar o modificar una disposición.`);
        return;
      }
    }

    if (!cantidadDispuesta || isNaN(Number(cantidadDispuesta)) || Number(cantidadDispuesta) <= 0) {
      setErrorMsg('Digite una cantidad válida mayor a cero.');
      return;
    }

    if (selectedOrden) {
      const totalDisposed = disposiciones
        .filter((d) => d.ordenId === selectedOrden.id && d.id !== editingDispId)
        .reduce((sum, d) => sum + d.cantidadDispuesta, 0);
      const pending = selectedOrden.tamanoLote - totalDisposed;

      if (Number(cantidadDispuesta) > pending) {
        setErrorMsg(`La cantidad dispuesta no puede superar las unidades pendientes de disposición (${pending} unidades).`);
        return;
      }
    }

    if (!observaciones.trim()) {
      setErrorMsg('Por favor registre las observaciones de la disposición.');
      return;
    }

    if (editingDispId) {
      if (onUpdateDisposicion) {
        const originalDisp = disposiciones.find(d => d.id === editingDispId);
        const ordId = selectedOrden ? selectedOrden.id : (originalDisp?.ordenId || '');
        const numOrd = selectedOrden ? selectedOrden.numeroOrden : (originalDisp?.numeroOrden || '');
        const nomMed = selectedOrden ? `${selectedOrden.nombreGenerico} (${selectedOrden.nombreComercial})` : (originalDisp?.nombreMedicamento || '');

        onUpdateDisposicion({
          id: editingDispId,
          ordenId: ordId,
          numeroOrden: numOrd,
          nombreMedicamento: nomMed,
          estadoDisposicion,
          cantidadDispuesta: Number(cantidadDispuesta),
          observaciones: observaciones.trim(),
          fechaDisposicion: new Date().toISOString(),
          responsable: currentUser.nombreCompleto,
        });
        setSuccessMsg(`Disposición para la orden ${numOrd} modificada con éxito.`);
        handleCancelEditDisp();
      }
      return;
    }

    if (selectedOrden) {
      const extra: Partial<OrdenProduccion> = {};
      if (!selectedOrden.fechaInicioReempaque) {
         extra.fechaInicioReempaque = new Date().toISOString();
      }
      // Se registra automáticamente con la fecha y hora actual del sistema al guardar
      extra.fechaFinReempaque = new Date().toISOString();
      
      onUpdateOrdenStatus(selectedOrden.id, selectedOrden.estadoOrden, extra);

      const nuevaDisp: DisposicionProducto = {
        id: String(disposiciones.length + 1),
        ordenId: selectedOrden.id,
        numeroOrden: selectedOrden.numeroOrden,
        nombreMedicamento: `${selectedOrden.nombreGenerico} (${selectedOrden.nombreComercial})`,
        estadoDisposicion,
        cantidadDispuesta: Number(cantidadDispuesta),
        observaciones: observaciones.trim(),
        fechaDisposicion: new Date().toISOString(),
        responsable: currentUser.nombreCompleto,
      };

      onAddDisposicion(nuevaDisp);
      setSuccessMsg(`Disposición para la orden ${selectedOrden.numeroOrden} registrada con éxito as "${estadoDisposicion}".`);
      
      // Reset
      setSelectedOrdenId('');
      setObservaciones('');
      setCantidadDispuesta('');
    }
  };

  const handleExportToExcel = () => {
    if (disposiciones.length === 0) return;

    // Excel compatible CSV with Spanish format (semicolon separator)
    // and Unicode UTF-8 BOM so Spanish letters and accents display perfectly.
    const headers = [
      'Código Disposición',
      'N° de Orden',
      'Medicamento',
      'Estado Técnico',
      'Cantidad Dispuesta (Unidades)',
      'Observaciones de Calidad',
      'Inspector Firmante',
      'Fecha Disposición'
    ];

    const rows = disposiciones.map(disp => [
      disp.id,
      disp.numeroOrden,
      disp.nombreMedicamento,
      disp.estadoDisposicion,
      disp.cantidadDispuesta,
      disp.observaciones,
      disp.responsable,
      new Date(disp.fechaDisposicion).toLocaleString('es-CO')
    ]);

    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.map(val => {
        const strVal = String(val ?? '');
        const escaped = strVal.replace(/"/g, '""');
        return `"${escaped}"`;
      }).join(';'))
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `INSUMEDIC_Liberaciones_Calidad_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadSinglePDF = async (disp: DisposicionProducto) => {
    const ord = ordenes.find((o) => o.id === disp.ordenId || o.numeroOrden === disp.numeroOrden);
    
    // Load logo first
    const loadLogo = () => {
      return new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.src = insumedicLogo;
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Failed to load logo'));
      });
    };

    let logoImg: HTMLImageElement | null = null;
    try {
      logoImg = await loadLogo();
    } catch (err) {
      console.error("Error cargando el logo de INSUMEDIC:", err);
    }

    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const margin = 15;
    let y = 15;

    // Header banner with deep slate/indigo color (Corporate pharmaceutical look)
    doc.setFillColor(30, 41, 59); // slate-800
    doc.rect(margin, y, 180, 26, 'F');

    // Draw white container on top-left of header and display attached INSUMEDIC logo
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(margin + 2, y + 2, 35, 22, 1.5, 1.5, 'F');
    if (logoImg) {
      try {
        doc.addImage(logoImg, 'PNG', margin + 3, y + 3, 33, 20);
      } catch (err) {
        console.error("Error setting logo image in PDF:", err);
      }
    }

    // Title / Corporate Text
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('COMERCIALIZADORA INSUMEDIC S.A.S. ZOMAC - NIT 901201223', margin + 40, y + 8);
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'normal');
    doc.text('Certificado de Liberación Técnica y Disposición de Productos', margin + 40, y + 13.5);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(191, 219, 254);
    doc.text(`Código Certificado: LC-${disp.id} | Fecha Emisión: ${new Date().toLocaleDateString('es-CO')} | Documento Oficial`, margin + 40, y + 19);

    y += 32;

    // Certificate text
    doc.setTextColor(51, 65, 85);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(7.5);
    doc.text(
      'Por medio de la presente acta de control, se certifica que el lote de reempaque descrito a continuación ha sido',
      margin,
      y
    );
    let dictamenText = 'evaluado bajo los lineamientos de Aseguramiento de Calidad de Comercializadora Insumedic S.A.S. Zomac, obteniendo un dictamen favorable de liberación.';
    if (disp.estadoDisposicion === 'Rechazado') {
      dictamenText = 'evaluado bajo los lineamientos de Aseguramiento de Calidad de Comercializadora Insumedic S.A.S. Zomac, obteniendo un dictamen desfavorable de RECHAZO por desviación de calidad.';
    } else if (disp.estadoDisposicion === 'En Cuarentena') {
      dictamenText = 'evaluado bajo los lineamientos de Aseguramiento de Calidad de Comercializadora Insumedic S.A.S. Zomac, quedando retenido preventivamente EN CUARENTENA para mayor estudio/análisis.';
    }
    doc.text(
      dictamenText,
      margin,
      y + 4
    );

    y += 9;

    // Heading: Section 1
    doc.setFillColor(241, 245, 249);
    doc.rect(margin, y, 180, 7, 'F');
    doc.setTextColor(30, 41, 59);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text('1. INTEGRACIÓN DE LA ORDEN DE PRODUCCIÓN ORIGEN', margin + 4, y + 4.5);

    y += 10;

    // Helper to draw cells
    const drawCell = (x: number, py: number, w: number, h: number, label: string, val: string) => {
      doc.setDrawColor(226, 232, 240);
      doc.rect(x, py, w, h, 'S');
      doc.setFillColor(250, 251, 252);
      doc.rect(x + 0.1, py + 0.1, w - 0.2, h - 0.2, 'F');
      doc.setTextColor(100, 116, 139);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.5);
      doc.text(label.toUpperCase(), x + 3, py + 4);
      doc.setTextColor(15, 23, 42);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      const maxChars = Math.floor(w * 0.45);
      const displayVal = val && val.length > maxChars ? val.substring(0, maxChars - 3) + '...' : (val || 'N/A');
      doc.text(displayVal, x + 3, py + 9);
    };

    // Row 1
    drawCell(15, y, 60, 12, 'Número de Orden', ord?.numeroOrden || disp.numeroOrden);
    drawCell(75, y, 60, 12, 'Lote RP INSUMEDIC', ord?.loteRP || 'N/A');
    drawCell(135, y, 60, 12, 'Lote Fabricante', ord?.loteFabricante || 'N/A');

    y += 12;

    // Row 2
    drawCell(15, y, 60, 12, 'Código Medicamento', ord?.codigoMedicamento || 'N/A');
    drawCell(75, y, 62, 12, 'Nombre Genérico', ord?.nombreGenerico || 'N/A');
    drawCell(137, y, 58, 12, 'Nombre Comercial', ord?.nombreComercial || 'N/A');

    y += 12;

    // Row 3
    drawCell(15, y, 60, 12, 'Concentración', ord?.concentracion || 'N/A');
    drawCell(75, y, 60, 12, 'Forma Farmacéutica', ord?.formaFarmaceutica || 'N/A');
    drawCell(135, y, 60, 12, 'Laboratorio Fabricante', ord?.laboratorioFabricante || 'N/A');

    y += 12;

    // Row 4
    drawCell(15, y, 60, 12, 'Registro Sanitario INVIMA', ord?.registroSanitario || 'N/A');
    drawCell(75, y, 60, 12, 'Código CUMS', ord?.cum || 'N/A');
    drawCell(135, y, 60, 12, 'Vencimiento de Lote', ord?.fechaVencimiento || 'N/A');

    y += 12;

    // Row 5
    drawCell(15, y, 90, 12, 'Tamaño del Lote (Unidades)', `${ord?.tamanoLote?.toLocaleString() || 'N/A'} ud.`);
    drawCell(105, y, 90, 12, 'Manejo Especial', ord?.manejoEspecial || 'NO');

    y += 12;

    // Row 6 (Auxiliar de Reempaque Information)
    drawCell(15, y, 60, 12, 'Auxiliar de Reempaque', ord?.elaboradoPor || 'N/A');
    drawCell(75, y, 60, 12, 'Fecha/Hora Inicio Reempaque', formatFechaHora(ord?.fechaInicioReempaque));
    drawCell(135, y, 60, 12, 'Fecha/Hora Fin Reempaque', formatFechaHora(ord?.fechaFinReempaque));

    y += 15;

    // Heading: Section 2
    doc.setFillColor(241, 245, 249);
    doc.rect(margin, y, 180, 7, 'F');
    doc.setTextColor(30, 41, 59);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text('2. AUDITORÍA FÍSICA Y DICTAMEN DE LIBERACIÓN TÉCNICA', margin + 4, y + 4.5);

    y += 8;

    // Row 6
    drawCell(15, y, 60, 12, 'Auditoría ID', disp.id);
    drawCell(75, y, 60, 12, 'Cantidad Acondicionada', `${disp.cantidadDispuesta.toLocaleString()} ud.`);
    
    // Status highlighted cell
    doc.setDrawColor(226, 232, 240);
    doc.rect(135, y, 60, 12, 'S');
    
    let highlightBgRGB = [236, 253, 245]; // emerald-50 base default
    let highlightTextRGB = [16, 185, 129]; // emerald-600 default
    
    if (disp.estadoDisposicion === 'Rechazado') {
      highlightBgRGB = [254, 242, 242]; // red-50
      highlightTextRGB = [220, 38, 38]; // red-600
    } else if (disp.estadoDisposicion === 'En Cuarentena') {
      highlightBgRGB = [254, 243, 199]; // amber-50
      highlightTextRGB = [217, 119, 6]; // amber-600
    }
    
    doc.setFillColor(highlightBgRGB[0], highlightBgRGB[1], highlightBgRGB[2]);
    doc.rect(135 + 0.1, y + 0.1, 60 - 0.2, 12 - 0.2, 'F');
    doc.setTextColor(highlightTextRGB[0], highlightTextRGB[1], highlightTextRGB[2]);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.5);
    doc.text('ESTADO DE DISPOSICIÓN', 138, y + 4);
    doc.setFontSize(8.5);
    doc.text(disp.estadoDisposicion.toUpperCase(), 138, y + 9.5);

    y += 12;

    // Large observations cell
    doc.setDrawColor(226, 232, 240);
    doc.rect(15, y, 180, 22, 'S');
    doc.setFillColor(250, 251, 252);
    doc.rect(15 + 0.1, y + 0.1, 180 - 0.2, 22 - 0.2, 'F');
    
    doc.setTextColor(100, 116, 139);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.5);
    doc.text('OBSERVACIONES DE DETALLE / DICTAMEN TÉCNICO', 18, y + 4.5);
    doc.setTextColor(15, 23, 42);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    const splitText = doc.splitTextToSize(disp.observaciones, 172);
    doc.text(splitText, 18, y + 9);

    y += 24;

    // Heading: Section 3
    doc.setFillColor(241, 245, 249);
    doc.rect(margin, y, 180, 7, 'F');
    doc.setTextColor(30, 41, 59);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text('3. RESPONSABLES DE VERIFICACIÓN Y CONTROL FÍSICO', margin + 4, y + 4.5);

    y += 8;

    // Auditor/inspector details
    drawCell(15, y, 90, 12, 'Inspector Firmante (Dictamen)', disp.responsable);
    drawCell(105, y, 90, 12, 'Fecha y Hora de Dictamen con Sello', formatFechaHora(disp.fechaDisposicion));

    y += 20;

    // Inject signature images with robust lookups and dynamic blue-ink cursive fallback
    const findUserRobustly = (nombre: string | undefined) => {
      if (!nombre) return undefined;
      return users.find(
        (u) => u.nombreCompleto.trim().toLowerCase() === nombre.trim().toLowerCase()
      );
    };

    const approvingUserObj = findUserRobustly(disp.responsable);

    // Find Director Técnico y de Calidad user to always use as inspector who registers
    const directorUserObj = users.find(
      (u) => u.cargo === 'Director Técnico y de Calidad' || u.username.toUpperCase() === 'MARIA.CECHAGUA'
    ) || findUserRobustly('MARIA ANGELICA CECHAGUA');

    const directorUserObjClean = directorUserObj || {
       id: 'default-director-id',
       nombreCompleto: 'MARIA ANGELICA CECHAGUA',
       cedula: '55069292',
       cargo: 'Director Técnico y de Calidad',
       username: 'MARIA.CECHAGUA',
       estado: 'ACTIVO',
       esAdmin: false,
       modulosPermitidos: [],
       companyId: 'company-insumedic'
    } as User;

    // Get signature image with dynamic ink-blue handwriting scribble as default fallback for authorized roles
    const getSignatureImageWithFallback = (userObj: User | null | undefined): string | null => {
      if (!userObj) return null;
      if (userObj.firma) return userObj.firma;

      // Generate fallback signature for any role
      const isAuthorizedRole = true;

      if (!isAuthorizedRole) return null;

      try {
        const canvas = document.createElement('canvas');
        canvas.width = 300;
        canvas.height = 100;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.clearRect(0, 0, 300, 100);
          
          // Traditional pharmaceutical blue-ink styling
          ctx.strokeStyle = '#1d4ed8'; // blue-700
          ctx.lineWidth = 1.8;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';

          // Personalized deterministic visual flourish based on user name
          let hash = 0;
          const name = userObj.nombreCompleto;
          for (let i = 0; i < name.length; i++) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
          }
          const absHash = Math.abs(hash);

          ctx.beginPath();
          let x = 40;
          const yCoord = 50;
          ctx.moveTo(x, yCoord);
          
          // First stylized capital letter swoop
          ctx.bezierCurveTo(x + 10, yCoord - 25, x + 20, yCoord + 25, x + 30, yCoord - 10);
          x += 30;

          // Stylized loops based on first name length
          const loops = Math.min(6, Math.max(3, name.split(' ')[0].length));
          for (let j = 0; j < loops; j++) {
            const nextX = x + 12 + (absHash % (j + 4));
            const topCurve = yCoord - 12 - (absHash % 8);
            const bottomCurve = yCoord + 12 + (absHash % 6);
            ctx.bezierCurveTo(x + 4, topCurve, nextX - 4, bottomCurve, nextX, yCoord - (absHash % 4));
            x = nextX;
          }

          // Flourish extension
          ctx.bezierCurveTo(x + 20, yCoord + 10, x + 30, yCoord - 20, x + 40, yCoord);
          ctx.stroke();

          // Elegant underline loop sweep
          ctx.beginPath();
          ctx.lineWidth = 1.3;
          ctx.moveTo(35, yCoord + 10 + (absHash % 5));
          ctx.bezierCurveTo(100, yCoord + 24, x, yCoord + 5, x + 35, yCoord + 14);
          ctx.stroke();

          return canvas.toDataURL('image/png');
        }
      } catch (e) {
        console.error("Error generating fallback signature:", e);
      }
      return null;
    };

    // Use director's signature for the registering inspector slot
    const creatorSignature = getSignatureImageWithFallback(directorUserObjClean);
    const approvingSignature = getSignatureImageWithFallback(approvingUserObj);

    if (creatorSignature) {
      try {
        doc.addImage(creatorSignature, 'PNG', 20, y - 18, 45, 14);
      } catch (err) {
        console.error("Error setting creator signature in PDF:", err);
      }
    }

    if (approvingSignature) {
      try {
        doc.addImage(approvingSignature, 'PNG', 115, y - 18, 45, 14);
      } catch (err) {
        console.error("Error setting approver signature in PDF:", err);
      }
    }

    // Lines for signatures
    doc.setDrawColor(148, 163, 184); // slate-400
    doc.line(15, y, 80, y);
    doc.line(110, y, 175, y);

    const directorNameString = directorUserObjClean.nombreCompleto;
    const directorCargoString = directorUserObjClean.cargo;

    doc.setTextColor(71, 85, 105);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.text(directorNameString, 15, y + 4.5);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.text(directorCargoString, 15, y + 8);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(5.5);
    doc.text('Firma de Inspector que Registra (Calidad)', 15, y + 11.5);

    doc.setTextColor(71, 85, 105);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.text('Firma de Aseguramiento de Calidad / Regente', 110, y + 4.5);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(6);
    doc.text('Concepto Técnico de Aprobación de Dispensación', 110, y + 8);

    // Footer
    doc.setTextColor(148, 163, 184);
    doc.setFontSize(6);
    doc.setFont('helvetica', 'normal');
    doc.text('Este certificado acredita que el lote ha cumplido el protocolo de reempaque y fraccionamiento de Comercializadora Insumedic S.A.S. Zomac NIT: 901201223.', 15, 282);
    doc.text(`Documento autogenerado digitalmente el ${new Date().toLocaleString('es-CO')}. Control de calidad asistencial con firma digital autorizada.`, 15, 285);

    doc.save(`INSUMEDIC_Liberado_Lote_${disp.numeroOrden}_ID${disp.id}.pdf`);
  };

  return (
    <div className="space-y-8 animate-fade-in" id="disposicion-productos-container">
      
      {/* Banner / Header */}
      <div className="bg-[#071A36] border border-[#1C4F8A]/50 text-white p-6 rounded-2xl shadow-md relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl pointer-events-none"></div>
        <h2 className="text-xl font-extrabold font-sans flex items-center text-white">
          <CheckSquare className="h-5.5 w-5.5 mr-2.5 text-sky-300" />
          DISPOSICIÓN MEDICAMENTOS
        </h2>
        <p className="text-sm text-slate-200 font-sans mt-1">
          Libere lotes finalizados asignando el estado técnico definitivo: Aprobado, Rechazado o En Cuarentena.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        
        {/* Disposición form (7 cols) */}
        <div className="lg:col-span-12 xl:col-span-8 bg-white p-6 rounded-2xl border border-slate-200 shadow-xs">
          <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider border-b border-slate-100 pb-2 mb-5">
            {editingDispId ? 'Modificar Liberación o Disposición de Lotes' : 'Registrar Liberación o Disposición de Lotes'}
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4" id="form-disposicion">
            {isReadOnly && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5 flex items-center gap-3 text-amber-900 text-xs font-sans font-bold shadow-2xs">
                <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
                <span>
                  🔒 <strong>Modo Consulta Activo:</strong> Licencia vencida. El registro y modificación de disposiciones están deshabilitados.
                </span>
              </div>
            )}
            {errorMsg && (
              <div className="bg-red-50 text-red-700 text-xs p-3 rounded-lg border border-red-100 font-sans">
                {errorMsg}
              </div>
            )}

            {successMsg && (
              <div className="bg-emerald-50 text-emerald-700 text-xs p-3 rounded-lg border border-emerald-100 font-sans">
                ✓ {successMsg}
              </div>
            )}

            {/* Select Order */}
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5 font-sans">
                Seleccionar Orden de Producción / Lote RP
              </label>
              <select
                required={!editingDispId}
                value={selectedOrdenId}
                onChange={(e) => setSelectedOrdenId(e.target.value)}
                className="w-full h-10 px-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500/10 focus:ring-blue-500 font-sans text-slate-800"
                id="select-orden-disponer"
              >
                <option value="">-- Seleccionar Orden de Producción registrado --</option>
                {ordenes.map((o) => {
                  // Check if order has already been fully disposed, excluding current editing disposition
                  const totalDisposed = disposiciones
                    .filter((d) => d.ordenId === o.id && d.id !== editingDispId)
                    .reduce((sum, d) => sum + d.cantidadDispuesta, 0);
                  
                  const pending = o.tamanoLote - totalDisposed;
                  if (pending <= 0 && o.id !== selectedOrdenId) return null;

                  return (
                    <option key={o.id} value={o.id}>
                      {o.numeroOrden} - {o.nombreGenerico} ({o.nombreComercial}) | Lote RP: {o.loteRP} (Pendiente: {pending} de {o.tamanoLote} ud.)
                    </option>
                  );
                })}
              </select>

              {/* Accept or Return Panel directly under the Select field */}
              {selectedOrden && (
                <div className="mt-3.5 p-3.5 bg-slate-50 border border-slate-200 rounded-xl space-y-3 font-sans">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div>
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">
                        Estado Actual de la Orden de Producción:
                      </span>
                      <span className={`inline-flex px-2.5 py-1 mt-1 rounded-full font-bold text-[10px] uppercase tracking-wider border shadow-xs ${
                        selectedOrden.estadoOrden === 'Pendiente por Aceptar'
                          ? 'bg-amber-50 text-amber-800 border-amber-100'
                          : selectedOrden.estadoOrden === 'Cancelada'
                          ? 'bg-rose-50 text-rose-800 border-rose-100'
                          : 'bg-emerald-50 text-emerald-800 border-emerald-100'
                      }`}>
                        {selectedOrden.estadoOrden || 'Pendiente por Aceptar'}
                      </span>
                    </div>

                    <div className="flex items-center gap-2.5">
                      <button
                        type="button"
                        onClick={() => {
                          onUpdateOrdenStatus(selectedOrden.id, 'Aceptada');
                          setSuccessMsg(`La Orden de Producción ${selectedOrden.numeroOrden} ha sido ACEPTADA para acondicionamiento.`);
                        }}
                        className={`px-3 py-1.5 rounded-lg text-[10px] font-extrabold uppercase tracking-wider transform transition-all duration-200 ease-out cursor-pointer border ${
                          selectedOrden.estadoOrden === 'Aceptada'
                            ? 'bg-blue-100 text-blue-800 border-blue-250 cursor-not-allowed shadow-none'
                            : 'bg-[#1C4F8A] hover:bg-[#071A36] hover:scale-[1.04] active:scale-[0.96] text-white border-transparent hover:shadow-xs'
                        }`}
                        disabled={selectedOrden.estadoOrden === 'Aceptada'}
                      >
                        Aceptar Orden
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          onUpdateOrdenStatus(selectedOrden.id, 'Cancelada');
                          setSuccessMsg(`La Orden de Producción ${selectedOrden.numeroOrden} ha sido CANCELADA.`);
                        }}
                        className={`px-3 py-1.5 rounded-lg text-[10px] font-extrabold uppercase tracking-wider transform transition-all duration-200 ease-out cursor-pointer border ${
                          selectedOrden.estadoOrden === 'Cancelada'
                            ? 'bg-rose-100 text-rose-800 border-rose-250 cursor-not-allowed shadow-none'
                            : 'bg-rose-600 hover:bg-rose-700 hover:scale-[1.04] active:scale-[0.96] text-white border-transparent hover:shadow-xs'
                        }`}
                        disabled={selectedOrden.estadoOrden === 'Cancelada'}
                      >
                        Cancelar Orden
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {selectedOrden && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }} 
                animate={{ opacity: 1, height: 'auto' }}
                className="bg-slate-50 p-4 rounded-xl border border-slate-200/50 space-y-4"
              >
                <div>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-2">
                    Información Técnica Seleccionada:
                  </span>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs font-sans">
                    <div>
                      <span className="text-slate-400 block">Lote de Fabricante:</span>
                      <strong className="text-slate-800 font-mono">{selectedOrden.loteFabricante}</strong>
                    </div>
                    <div>
                      <span className="text-slate-400 block">Laboratorio:</span>
                      <strong className="text-slate-800">{selectedOrden.laboratorioFabricante}</strong>
                    </div>
                    <div>
                      <span className="text-slate-400 block">Registro Invima:</span>
                      <strong className="text-slate-800 font-mono text-[10px]">{selectedOrden.registroSanitario}</strong>
                    </div>
                    <div>
                      <span className="text-slate-400 block">Manejo Especial:</span>
                      <span className={`font-bold ${
                        selectedOrden.manejoEspecial === 'SI' 
                          ? 'text-purple-600' 
                          : selectedOrden.manejoEspecial === 'FOTOSENSIBLE' 
                          ? 'text-amber-600' 
                          : 'text-slate-600'
                      }`}>
                        {selectedOrden.manejoEspecial === 'FOTOSENSIBLE' ? 'SÍ (FOTOSENSIBLE)' : selectedOrden.manejoEspecial}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="border-t border-slate-200/80 pt-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">
                      Información del Auxiliar y Tiempos de Reempaque:
                    </span>
                    <span className="text-[9px] text-blue-600 font-bold bg-blue-50 border border-blue-100 px-2 py-0.5 rounded-full">
                      Control de Proceso Obligatorio
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 font-sans">
                        Auxiliar de Reempaque
                      </label>
                      <input
                        type="text"
                        className="w-full h-8 px-2.5 text-xs bg-white border border-slate-200 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500/10 focus:ring-blue-500 font-sans text-slate-800 font-semibold"
                        value={selectedOrden.elaboradoPor || ''}
                        onChange={(e) => {
                          onUpdateOrdenStatus(selectedOrden.id, selectedOrden.estadoOrden, { elaboradoPor: e.target.value });
                        }}
                        placeholder="Nombre del Auxiliar"
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 font-sans">
                        Fecha y Hora de Inicio
                      </label>
                      {selectedOrden.fechaInicioReempaque ? (
                        <div className="flex items-center gap-1.5 h-8">
                          <div className="grow bg-emerald-50 text-emerald-800 font-mono text-[11px] font-bold rounded-lg px-2.5 flex items-center h-full border border-emerald-100 whitespace-nowrap overflow-hidden">
                            <span className="mr-1 text-emerald-500">⏱</span>
                            {new Date(selectedOrden.fechaInicioReempaque).toLocaleString('es-CO', { 
                              day: '2-digit', 
                              month: '2-digit', 
                              year: 'numeric', 
                              hour: '2-digit', 
                              minute: '2-digit', 
                              second: '2-digit' 
                            })}
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              onUpdateOrdenStatus(selectedOrden.id, selectedOrden.estadoOrden, { fechaInicioReempaque: new Date().toISOString() });
                            }}
                            className="bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold text-[10px] uppercase h-full px-2.5 rounded-lg flex items-center justify-center shrink-0 cursor-pointer transform hover:scale-[1.04] active:scale-[0.96] transition-all duration-200 ease-out border border-slate-300"
                            title="Actualizar a la hora actual"
                          >
                            Reiniciar
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            onUpdateOrdenStatus(selectedOrden.id, selectedOrden.estadoOrden, { fechaInicioReempaque: new Date().toISOString() });
                          }}
                          className="w-full h-8 bg-[#1C4F8A] hover:bg-[#071A36] text-white font-bold text-xs uppercase tracking-wider rounded-lg flex items-center justify-center gap-1.5 cursor-pointer transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 ease-out shadow-3xs"
                        >
                          <Play className="h-3 w-3" />
                          <span>Iniciar</span>
                        </button>
                      )}
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 font-sans">
                        Fecha y Hora de Cierre
                      </label>
                      {selectedOrden.fechaFinReempaque ? (
                        <div className="bg-blue-50 text-blue-800 font-mono text-[11px] font-bold rounded-lg px-2.5 flex items-center h-8 border border-blue-100 whitespace-nowrap overflow-hidden">
                          <span className="mr-1 text-blue-500">🏁</span>
                          {formatFechaHora(selectedOrden.fechaFinReempaque)}
                        </div>
                      ) : (
                        <div className="bg-slate-100 text-slate-500 font-mono text-[10px] rounded-lg px-2.5 flex items-center h-8 border border-slate-200 italic whitespace-nowrap overflow-hidden">
                          <span>Se fijará al registrar disposición</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              
              {/* State */}
              <div className="flex flex-col justify-between space-y-4">
                {currentUser.cargo === 'Auxiliar de Reempaque' ? (
                  <div className="bg-amber-50 border border-amber-200/50 p-3 rounded-lg text-xs leading-relaxed text-amber-900 font-sans flex items-start gap-2">
                    <span className="text-sm shrink-0">⚠️</span>
                    <span><strong>Rol Auxiliar:</strong> No tiene privilegios para asignar un dictamen técnico final. El lote será validado posteriormente por Aseguramiento de Calidad.</span>
                  </div>
                ) : (
                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                      Decisión de Disposición
                    </label>
                    <select
                      required
                      value={estadoDisposicion}
                      onChange={(e) => setEstadoDisposicion(e.target.value as any)}
                      className="w-full h-10 px-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500/10 focus:ring-blue-500 font-sans text-slate-800"
                      id="select-estado-disposicion"
                    >
                      <option value="Aprobado">Aprobado (Apto para dispensación / stock)</option>
                      <option value="Rechazado">Rechazado (Defectuoso o no apto)</option>
                      <option value="En Cuarentena">En Cuarentena (Requiere auditoría o reanálisis)</option>
                    </select>
                  </div>
                )}

                {/* Display N° Orden under the decision dropdown */}
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 font-sans">
                    Número de Orden Asociada
                  </label>
                  <div className="bg-slate-100 text-slate-800 font-mono text-xs font-bold rounded-lg px-3 flex items-center h-10 border border-slate-200 shadow-3xs">
                    {selectedOrden 
                      ? (selectedOrden.fechaInicioReempaque 
                          ? selectedOrden.numeroOrden 
                          : 'Pendiente (Se generará al Iniciar Proceso)') 
                      : 'No se ha seleccionado ninguna orden'}
                  </div>
                </div>
              </div>

              {/* Disposed Qty */}
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  Cantidad a Disponer (Unidosis)
                </label>
                <input
                  type="number"
                  required
                  value={cantidadDispuesta}
                  onChange={(e) => setCantidadDispuesta(e.target.value)}
                  placeholder="Por ej. 50"
                  className="w-full h-10 px-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500/10 focus:ring-blue-500 font-sans text-slate-800 font-mono"
                  id="input-cantidad-disponer"
                />
              </div>

            </div>

            {/* Observations */}
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                Observaciones y Justificación de Calidad
              </label>
              <textarea
                required
                rows={3}
                value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)}
                placeholder="Introduzca el motivo del rechazo, comentarios de cuarentena o especificaciones sobre la aprobación técnica..."
                className="w-full p-3 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500/10 focus:ring-blue-500 font-sans text-slate-800"
                id="textarea-observaciones-disposicion"
              />
            </div>

            <div className="flex items-center space-x-2 text-slate-500 text-xs py-1">
              <UserCheck className="h-4 w-4 text-emerald-600" />
              <span>Responsable Firmante: <strong>{currentUser.nombreCompleto}</strong> ({currentUser.cargo})</span>
            </div>

            {editingDispId ? (
              <div className="flex gap-4 pt-2">
                <button
                  type="submit"
                  className="grow h-10 bg-amber-600 hover:bg-amber-700 active:bg-amber-800 text-white font-sans text-xs font-bold uppercase tracking-wider rounded-lg transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 ease-out shadow-sm cursor-pointer flex items-center justify-center space-x-2"
                  id="btn-edit-disposicion-save"
                >
                  <CheckSquare className="h-4 w-4" />
                  <span>Guardar Cambios de la Disposición</span>
                </button>
                <button
                  type="button"
                  onClick={handleCancelEditDisp}
                  className="px-4 h-10 bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300 font-sans text-xs font-bold uppercase tracking-wider rounded-lg transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 ease-out cursor-pointer flex items-center justify-center"
                  id="btn-edit-disposicion-cancel"
                >
                  Cancelar Edición
                </button>
              </div>
            ) : currentUser.cargo === 'Auxiliar de Reempaque' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    if (!selectedOrdenId || !selectedOrden) {
                      setErrorMsg('Debe seleccionar una Orden de Producción activa.');
                      return;
                    }
                    const updateObj: Partial<OrdenProduccion> = {};
                    if (!selectedOrden.fechaInicioReempaque) {
                      updateObj.fechaInicioReempaque = new Date().toISOString();
                    }
                    onUpdateOrdenStatus(selectedOrden.id, 'Aceptada', updateObj);
                    setSuccessMsg(`✓ Proceso de reempaque INICIADO para la Orden ${selectedOrden.numeroOrden} correctamente.`);
                  }}
                  className="h-10 bg-[#1C4F8A] hover:bg-[#071A36] text-white font-sans text-xs font-bold uppercase tracking-wider rounded-lg transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 ease-out shadow-xs cursor-pointer flex items-center justify-center space-x-2"
                  id="btn-iniciar-reempaque"
                >
                  <Plus className="h-4 w-4" />
                  <span>Iniciar Proceso de Reempaque</span>
                </button>
                
                <button
                  type="button"
                  onClick={() => {
                    if (!selectedOrdenId || !selectedOrden) {
                      setErrorMsg('Debe seleccionar una Orden de Producción activa.');
                      return;
                    }
                    const updateObj: Partial<OrdenProduccion> = {};
                    if (!selectedOrden.fechaFinReempaque) {
                      updateObj.fechaFinReempaque = new Date().toISOString();
                    }
                    onUpdateOrdenStatus(selectedOrden.id, 'Aceptada', updateObj);
                    setSuccessMsg(`✓ El lote ${selectedOrden.loteRP} de la orden ${selectedOrden.numeroOrden} ha sido enviado a validación técnica del Regente o Director.`);
                  }}
                  className="h-10 bg-blue-600 hover:bg-blue-700 text-white font-sans text-xs font-bold uppercase tracking-wider rounded-lg transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 ease-out shadow-xs cursor-pointer flex items-center justify-center space-x-2"
                  id="btn-pasar-validacion"
                >
                  <UserCheck className="h-4 w-4" />
                  <span>Pasar a Validación</span>
                </button>
              </div>
            ) : (
              <button
                type="submit"
                className="w-full h-10 bg-indigo-600 hover:bg-indigo-700 text-white font-sans text-xs font-bold uppercase tracking-wider rounded-lg transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 ease-out shadow-sm cursor-pointer flex items-center justify-center space-x-2"
                id="btn-submit-disposicion"
              >
                <Plus className="h-4 w-4" />
                <span>Registrar Disposición Técnica</span>
              </button>
            )}
          </form>
        </div>

        {/* Quality status summary counters (4 cols) */}
        <div className="lg:col-span-12 xl:col-span-4 space-y-6">
          <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 space-y-4">
            <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center">
              <Clipboard className="h-4.5 w-4.5 text-blue-600 mr-2" />
              Estados de Liberación
            </h3>

            <div className="grid grid-cols-1 gap-3">
              
              <div className="bg-emerald-50/60 p-3 rounded-xl border border-emerald-100 flex items-center justify-between">
                <div className="flex items-center space-x-2.5">
                  <div className="p-1 px-1.5 bg-emerald-100 text-emerald-800 rounded font-bold text-xs">OK</div>
                  <span className="text-xs text-slate-700 font-sans">Aprobado</span>
                </div>
                <strong className="text-emerald-800 font-mono text-sm">
                  {disposiciones.filter(d => d.estadoDisposicion === 'Aprobado').reduce((sum, d) => sum + d.cantidadDispuesta, 0)} ud.
                </strong>
              </div>

              <div className="bg-amber-50/60 p-3 rounded-xl border border-amber-100 flex items-center justify-between">
                <div className="flex items-center space-x-2.5">
                  <div className="p-1 px-1.5 bg-amber-100 text-amber-800 rounded font-bold text-xs font-mono">CR</div>
                  <span className="text-xs text-slate-700 font-sans">En Cuarentena</span>
                </div>
                <strong className="text-amber-800 font-mono text-sm">
                  {disposiciones.filter(d => d.estadoDisposicion === 'En Cuarentena').reduce((sum, d) => sum + d.cantidadDispuesta, 0)} ud.
                </strong>
              </div>

              <div className="bg-red-50/60 p-3 rounded-xl border border-red-100 flex items-center justify-between">
                <div className="flex items-center space-x-2.5">
                  <div className="p-1 px-1.5 bg-red-100 text-red-800 rounded font-bold text-xs font-mono">RE</div>
                  <span className="text-xs text-slate-700 font-sans">Rechazado</span>
                </div>
                <strong className="text-red-800 font-mono text-sm">
                  {disposiciones.filter(d => d.estadoDisposicion === 'Rechazado').reduce((sum, d) => sum + d.cantidadDispuesta, 0)} ud.
                </strong>
              </div>

            </div>
          </div>
        </div>

      </div>

      {/* Dispositions list table */}
      <div className="bg-white p-6 rounded-2xl border border-slate-200/90 shadow-sm" id="seccion-historico-disposiciones">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-slate-100 pb-4 mb-4 gap-2">
          <div>
            <h3 className="font-sans font-extrabold text-slate-900 text-base">
              Historial de Disposiciones y Liberaciones
            </h3>
            <p className="text-xs text-slate-500 font-sans">
              Histórico de auditoría física de fraccionamientos concluidos en Comercializadora Insumedic S.A.S. Zomac.
            </p>
          </div>
          <div className="flex items-center space-x-3 self-stretch sm:self-auto justify-between sm:justify-start">
            {disposiciones.length > 0 && (
              <button
                onClick={handleExportToExcel}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-sans text-xs font-bold rounded-lg transform hover:scale-[1.04] active:scale-[0.96] transition-all duration-200 ease-out cursor-pointer shadow-sm hover:shadow-md"
                title="Exportar auditorías registradas a Excel CSV"
                id="btn-export-disposiciones-excel"
              >
                <FileSpreadsheet className="h-4 w-4" />
                <span>Exportar a Excel</span>
              </button>
            )}
            <span className="px-2.5 py-1 bg-slate-100 text-slate-700 font-mono text-[10px] rounded-full font-bold font-sans">
              Total Auditorías: {disposiciones.length}
            </span>
          </div>
        </div>

        {/* Quick Search Bar for Disposiciones / OP */}
        {disposiciones.length > 0 && (
          <div className="relative mb-4">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
              <Search className="h-4 w-4" />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscador rápido por N° Orden (OP), Medicamento, Inspector o Cód. Disposición..."
              className="w-full pl-9 pr-8 py-2 text-xs bg-slate-50 border border-slate-200/90 rounded-xl focus:outline-hidden focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-sans text-slate-800 placeholder-slate-400 shadow-3xs"
              id="input-search-disposiciones"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute inset-y-0 right-0 pr-2.5 flex items-center text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
                title="Limpiar búsqueda"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        )}

        {disposiciones.length === 0 ? (
          <div className="text-center py-10 text-slate-400 text-xs font-sans">
            No se han registrado disposiciones de lotes en esta sesión. Use el panel superior para realizar una liberación.
          </div>
        ) : filteredDisposiciones.length === 0 ? (
          <div className="text-center py-10 text-slate-500 text-xs font-sans bg-slate-50/50 rounded-xl border border-dashed border-slate-200">
            No se encontraron disposiciones que coincidan con "<strong className="font-semibold text-slate-700">{searchTerm}</strong>".
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse" id="tabla-disposiciones">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="p-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider font-mono">Cód. Disposición</th>
                  <th className="p-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider font-mono">N° Orden</th>
                  <th className="p-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider font-sans">Medicamento</th>
                  <th className="p-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider text-center font-sans">Estado Técnico</th>
                  <th className="p-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider text-right font-sans">Cant. Dispuesto</th>
                  <th className="p-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider font-sans">Observaciones de Calidad</th>
                  <th className="p-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider text-center font-sans">Certificado PDF</th>
                  <th className="p-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider font-sans">Inspector Firmante / Fecha</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredDisposiciones.map((disp, i) => (
                  <tr key={disp.id} className="hover:bg-slate-50 transition-colors" id={`row-disp-${disp.id}`}>
                    <td className="p-3 text-xs font-mono text-slate-500">
                      {disp.id}
                    </td>
                    
                    <td className="p-3 text-xs font-mono font-bold text-slate-800">
                      {disp.numeroOrden}
                    </td>

                    <td className="p-3 text-xs font-sans text-slate-700 font-semibold">
                      {disp.nombreMedicamento}
                    </td>

                    <td className="p-3 text-xs text-center">
                      <span className={`inline-flex px-2 py-1 rounded-sm text-[10px] font-bold border ${
                        disp.estadoDisposicion === 'Aprobado'
                          ? 'bg-emerald-50 text-emerald-800 border-emerald-100'
                          : disp.estadoDisposicion === 'Rechazado'
                          ? 'bg-red-50 text-red-800 border-red-100'
                          : 'bg-amber-50 text-amber-800 border-amber-100'
                      }`}>
                        {disp.estadoDisposicion.toUpperCase()}
                      </span>
                    </td>

                    <td className="p-3 text-xs text-right font-mono font-bold text-slate-800">
                      {disp.cantidadDispuesta.toLocaleString()} ud.
                    </td>

                    <td className="p-3 text-xs font-sans text-slate-600 max-w-sm shrink truncate" title={disp.observaciones}>
                      {disp.observaciones}
                    </td>

                    <td className="p-3 text-center shrink-0 flex items-center justify-center gap-1.5">
                      <button
                        onClick={() => handleDownloadSinglePDF(disp)}
                        className={`inline-flex items-center gap-1 px-2.5 py-1 text-white text-[10px] font-bold rounded-lg transform hover:scale-[1.08] active:scale-[0.92] transition-all duration-200 ease-out cursor-pointer shadow-xs hover:shadow-md ${
                          disp.estadoDisposicion === 'Aprobado'
                            ? 'bg-emerald-600 hover:bg-emerald-700'
                            : disp.estadoDisposicion === 'Rechazado'
                            ? 'bg-red-600 hover:bg-red-700'
                            : 'bg-amber-600 hover:bg-amber-700'
                        }`}
                        title={`Descargar Certificado de Disposición de Lote (${disp.estadoDisposicion})`}
                      >
                        <Download className="h-3 w-3" />
                        <span>PDF</span>
                      </button>
                      {(currentUser.username.toUpperCase() === 'JUAN.VALENCIA' || currentUser.cargo === 'Director Técnico y de Calidad') && (
                        <button
                          onClick={() => handleStartEditDisp(disp)}
                          className="p-1 text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded-md transform hover:scale-110 active:scale-90 transition-all duration-200 ease-out cursor-pointer border border-transparent hover:border-amber-100"
                          title="Editar Disposición / Medicamento Liberado"
                          id={`btn-edit-disp-${disp.id}`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </td>

                    <td className="p-3 text-xs font-sans">
                      <div className="font-semibold text-slate-850">{disp.responsable}</div>
                      <div className="text-[10px] text-slate-500 flex items-center mt-0.5 font-mono">
                        <Calendar className="h-3.5 w-3.5 mr-1 text-slate-400 shrink-0" />
                        {formatColombiaDateTime(disp.fechaDisposicion, true)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  );
}
